import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Stack,
  CircularProgress,
  Slide,
} from "@mui/material";

import { apiBaseUrl } from "../config-global";
import { toast } from "react-toastify";

/* -------------------- */
/* Initial State */
/* -------------------- */
const initialFormData = {
  name_en: "",
  name_ar: "",
  display_name: "",
  domain: "",
  website: "",
  vat: "",
  cr: "",
  admin_f_name: "",
  admin_l_name: "",
  admin_email: "",
  admin_country_code: "+966",
  admin_tel: "",
};

/* -------------------- */
/* Transition */
/* -------------------- */
const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

/* -------------------- */
/* Component */
/* -------------------- */
export default function BusinessRegistrationDialog({ open, onClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  const steps = ["Company Info", "Admin Info"];

  /* -------------------- */
  /* Helpers */
  /* -------------------- */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const isStepValid = () => {
    if (activeStep === 0)
      return formData.name_en && formData.name_ar && formData.display_name;

    if (activeStep === 1)
      return (
        formData.admin_f_name &&
        formData.admin_l_name &&
        formData.admin_email &&
        formData.admin_country_code &&
        formData.admin_tel
      );

    return false;
  };

  const reset = () => {
    setActiveStep(0);
    setFormData(initialFormData);
  }

  /* -------------------- */
  /* Submit */
  /* -------------------- */
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}v1business/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      toast.success("Registration successful!");
      onClose();
      reset();
    } catch (e) {
      toast.error(e.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- */
  /* Render */
  /* -------------------- */
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
    >
      <DialogTitle>Business Registration</DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((s) => (
            <Step key={s}>
              <StepLabel>{s}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* STEP CONTENT */}
        <Box mt={3}>
          {/* COMPANY */}
          {activeStep === 0 && (
            <>
              <TextField
                fullWidth
                label="Name (English)"
                name="name_en"
                value={formData.name_en}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Name (Arabic)"
                name="name_ar"
                value={formData.name_ar}
                onChange={handleChange}
                sx={{ mt: 2 }}
              />
              <TextField
                fullWidth
                label="Display Name (VAT)"
                name="display_name"
                value={formData.display_name}
                onChange={handleChange}
                sx={{ mt: 2 }}
              />
              <TextField
                fullWidth
                label="VAT Number"
                name="vat"
                value={formData.vat}
                onChange={handleChange}
                sx={{ mt: 2 }}
              />
              <TextField
                fullWidth
                label="Commercial Registration Number"
                name="cr"
                value={formData.cr}
                onChange={handleChange}
                sx={{ mt: 2 }}
              />
              <TextField
                fullWidth
                label="Company Domain"
                name="domain"
                value={formData.domain}
                onChange={handleChange}
                sx={{ mt: 2 }}
              />
              <TextField
                fullWidth
                label="Company Website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                sx={{ mt: 2 }}
              />
            </>
          )}

          {/* ADMIN */}
          {activeStep === 1 && (
            <>
              <TextField
                fullWidth
                label="Admin First Name"
                name="admin_f_name"
                onChange={handleChange}
                value={formData.admin_f_name}
              />
              <TextField
                fullWidth
                label="Admin Last Name"
                name="admin_l_name"
                onChange={handleChange}
                sx={{ mt: 2 }}
                value={formData.admin_l_name}
              />
              <TextField
                fullWidth
                label="Admin Email"
                name="admin_email"
                onChange={handleChange}
                sx={{ mt: 2 }}
              />

              <Stack direction="row" spacing={2} mt={2} sx={{ width: "100%" }}>
                {/* <Autocomplete
                  options={countries}
                  getOptionLabel={(o) => `+${o.phone}`}
                  onChange={(_, v) =>
                    setFormData((p) => ({
                      ...p,
                      admin_country_code: v ? `+${v.phone}` : "",
                    }))
                  }
                  renderInput={(p) => <TextField {...p} label="Country Code" />}
                /> */}
                <TextField
                  label="Country Code"
                  name="admin_country_code"
                  onChange={handleChange}
                  disabled
                  value={formData.admin_country_code}
                  focused
                />
                <TextField
                  label="Telephone"
                  name="admin_tel"
                  onChange={handleChange}
                  value={formData.admin_tel}
                  fullWidth
                />
              </Stack>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          disabled={activeStep === 0}
          onClick={() => setActiveStep((s) => s - 1)}
        >
          Back
        </Button>
        <Button
          variant="contained"
          disabled={!isStepValid() || loading}
          onClick={
            activeStep === steps.length - 1
              ? handleSubmit
              : () => setActiveStep((s) => s + 1)
          }
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : activeStep === 1 ? (
            "Submit"
          ) : (
            "Continue"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
